// Version: 1.7
// v1.7: A 403 only signs the user out when the API says the CSRF token is invalid.
//       Any other 403 ("Forbidden", e.g. a client page asking for an admin-only
//       list) goes back to the page. Clients opening Device Hub or Productivity Hub
//       were signed out, because those pages ask for admin-only data
//       (discovered_subnets, watched-accounts, alerts config) and every 403 was
//       treated as an expired CSRF token.
// v1.6: Refreshes the user's name from /api/auth/me when the app loads.
// v1.5: Keeps the user's name from the login response (app.py v4.00).
import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('stg_user');
    if (stored) {
      try { setUser(JSON.parse(stored)); } catch {}
      // v1.6: pick up the user's name (and a changed one) for sessions saved
      // before names existed or before it was set on the Users page
      fetch('/api/auth/me', { credentials: 'include' })
        .then(r => (r.ok ? r.json() : null))
        .then(d => {
          if (!d) return;
          setUser(u => {
            if (!u || (u.name || '') === (d.name || '')) return u;
            const nu = { ...u, name: d.name || '' };
            try { localStorage.setItem('stg_user', JSON.stringify(nu)); } catch {}
            return nu;
          });
        })
        .catch(() => {});
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
      credentials: 'include'
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed');
    if (data.mfa_required) return data;
    loginComplete(data);
    return data;
  };

  // Step 1 of Microsoft sign-in: ask the API for the Microsoft authorize URL,
  // then send the browser there. Microsoft will redirect back to
  // /auth/microsoft/callback with ?code=...&state=...
  const startMicrosoftLogin = async () => {
    const res = await fetch('/api/auth/microsoft/start', { credentials: 'include' });
    const data = await res.json();
    if (!res.ok || !data.auth_url) throw new Error(data.error || 'Could not start Microsoft sign-in');
    window.location.href = data.auth_url;
  };

  // Step 2 of Microsoft sign-in: called by the MicrosoftCallback page once
  // Microsoft has redirected back with a code+state. Returns the same shape
  // as login() — either {mfa_required: true, ...} or completes the session.
  const completeMicrosoftLogin = async (code, state) => {
    const res = await fetch('/api/auth/microsoft/callback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, state }),
      credentials: 'include'
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Microsoft sign-in failed');
    if (data.mfa_required) return data;
    loginComplete(data);
    return data;
  };

  const loginComplete = (data) => {
    // Store CSRF token and non-sensitive user info only
    // JWT is in httpOnly cookie — not accessible to JavaScript
    localStorage.setItem('stg_csrf', data.csrf_token || '');
    const userObj = {
      username: data.username,
      name: data.name || '',
      role: data.role,
      client_slug: data.client_slug || '',
      permissions: data.permissions || [],
      custom_role_name: data.custom_role_name || '',
      password_change_required: data.password_change_required || false
    };
    localStorage.setItem('stg_user', JSON.stringify(userObj));
    setUser(userObj);
  };

  const logout = async () => {
    try {
      await authFetch('/api/auth/logout', { method: 'POST' });
    } catch {}
    localStorage.removeItem('stg_csrf');
    localStorage.removeItem('stg_user');
    setUser(null);
  };

  const clearPasswordChangeRequired = () => {
    const updated = { ...user, password_change_required: false };
    localStorage.setItem('stg_user', JSON.stringify(updated));
    setUser(updated);
  };

  return (
    <AuthContext.Provider value={{ user, login, loginComplete, logout, loading, clearPasswordChangeRequired, startMicrosoftLogin, completeMicrosoftLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }

export async function authFetch(path, options = {}, _isRetry = false) {
  const csrfToken = localStorage.getItem('stg_csrf') || '';
  // Don't set Content-Type for FormData — browser must set it with the multipart boundary
  const isFormData = options.body instanceof FormData;
  const res = await fetch(path, {
    ...options,
    credentials: 'include',
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      'X-CSRF-Token': csrfToken,
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) {
    localStorage.removeItem('stg_csrf');
    localStorage.removeItem('stg_user');
    window.location.href = '/login';
    return res;
  }
  // CSRF token expired or evicted from Redis — refresh and retry once.
  // v1.7: only for the CSRF refusal; other 403s are returned to the caller.
  let csrfRefused = false;
  if (res.status === 403) {
    try { csrfRefused = /csrf/i.test((await res.clone().json()).error || ''); } catch {}
  }
  if (csrfRefused && !_isRetry) {
    try {
      const refresh = await fetch('/api/auth/csrf-refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }
      });
      if (refresh.ok) {
        const data = await refresh.json();
        localStorage.setItem('stg_csrf', data.csrf_token || '');
        return authFetch(path, options, true);  // retry original request once
      }
    } catch {}
    // Refresh also failed — session is unrecoverable, redirect to login
    localStorage.removeItem('stg_csrf');
    localStorage.removeItem('stg_user');
    window.location.href = '/login';
    return res;
  }
  return res;
}

export async function getSseToken() {
  try {
    const res = await authFetch('/api/auth/sse-token', { method: 'POST' });
    const data = await res.json();
    return data.sse_token || '';
  } catch {
    return '';
  }
}